import * as React from 'react';
import { ActionPointData, PublicActionPlanLink, fieldValidation, ExternalUser } from '../../../amorg-common';
import { updateActionPoint } from '../../../api/actionPointClientApi';
import { Button } from '../../../designSystem/button/Button';
import { EditableText } from '../../../designSystem/compositeComponents/EditableText';
import { EditableTextArea } from '../../../designSystem/compositeComponents/EditableTextArea';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { iconName, Icon } from '../../../designSystem/icons/Icons';
import { DateInput } from '../../../designSystem/inputs/DateInput';
import { OnOff } from '../../../designSystem/inputs/OnOff';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { ProtectedPageContext } from '../../_contexts/ProtectedPageContext';
import { ActionPlanLinkEditor } from '../../actionplans/components/ActionPlanLinkEditor';
import { Card } from '../../../designSystem/Card';

export type ActionPointMode = 
    { type: 'actionPlanTopic', topicId: string;} | 
    { type: 'agendaItem', topicId: string | null, ensureTopic: () => Promise<string | null>; };

type ActionPointRowProps = {
    mode:ActionPointMode,
    actionPoint:ActionPointData,
    disabled:boolean,
    linkDisplay?:{
        siteId?:string,
        actionPlanId:string,
        links:PublicActionPlanLink[]
    },
    onUpdated: () => void,
    onDelete:() => void,
    onSelectUser:() => void
}
export function ActionPointRow(props:ActionPointRowProps):JSX.Element
{
    const [loading, setLoading] = React.useState(false);

    const colors = React.useContext(ProtectedPageContext).colors;

    const ap = props.actionPoint;

    let icon: { name: iconName, color: string; } = { name: 'clock', color: colors.icon.passive };
    if (ap.done)
        icon = { name: 'status_ok', color: colors.icon.success };
    else if (!ap.done && ap.deadline && new Date(ap.deadline) < new Date())
        icon = { name: 'status_error', color: colors.icon.error };

    
    
    async function handleUpdateActionPoint (ap: ActionPointData)
    {
        setLoading(true);
        await updateActionPoint(ap.id, ap.description, ap.assignedUser, ap.deadline, ap.done);
        setLoading(false);

        props.onUpdated();
    }

    const col1 =    <div style={{ display:'flex', alignItems: 'center', flex: '1 0 300px' }}>
                        
                        <Icon name={icon.name} color={icon.color} />

                        <EditableTextArea
                            disabled={props.disabled}
                            placeholder=""
                            ariaLabel={"Handlepunkt beskrivelse"}
                            value={ap.description}
                            hideHoverEditButton
                            onSave={(value, isValid) => isValid && handleUpdateActionPoint({ ...ap, description: value })}
                            validator={(value) => fieldValidation.actionPoint.description(value)
                                ? { valid: true }
                                : { valid: false, errorMessage: `Skal udfyldes. Må ikke overstige 200 (${value.length}) tegn.` }
                            }
                        />
                    </div>;

    const col2 =    <Flex alignItems="center" style={{ width: '240px' }}>
                        
                        <Icon name="interface_user_user" />

                        {props.mode.type === 'actionPlanTopic' &&
                            
                                <div style={{ flex: '1 1 100px' }}>
                                    <EditableText
                                        fontSize={14}
                                        disabled={props.disabled}
                                        ariaLabel={"Vælg ejer for handlepunkt"}
                                        value={ap.assignedUser === null ? '' : (ap.assignedUser as ExternalUser).fullName}
                                        placeholder={"Vælg ejer"}
                                        hideHoverEditButton
                                        onSave={(value, isValid) =>
                                        {
                                            isValid && handleUpdateActionPoint({
                                                ...ap,
                                                assignedUser: value === ''
                                                    ? null
                                                    : { type: 'external', email: '', fullName: value, role: '' }
                                            });
                                        }}
                                        validator={(value) => value === '' || fieldValidation.actionPoint.assignedExternalUserName(value)
                                            ? { valid: true }
                                            : { valid: false, errorMessage: `Ugyldigt navn. Maks. 50 tegn.` }
                                        }
                                    />
                                </div>
                        }
                        
                        {props.mode.type === 'agendaItem' &&
                                <div style={{ flexGrow: 1 }}>
                                    <Button variant="clean"
                                        text={ap.assignedUser === null ? 'Vælg ejer' : ap.assignedUser.fullName}
                                        disabled={props.disabled}
                                        onClick={() => props.onSelectUser()}
                                    />
                                </div>
                        }

                    </Flex>;

    const col3 =    <Flex style={{ flex: '0 0 auto' }} alignItems="center">
                        <Icon name="calendar" />
                        <DateInput noBorder
                            disabled={props.disabled}
                            placeHolder="Vælg deadline"
                            value={ap.deadline}
                            onValueChange={(value: Date | null) => handleUpdateActionPoint({ ...ap, deadline: value })}
                            showClearButton
                            noIcon
                            fontSize={14}
                        />
                    </Flex>;


                        

                        

    const col4 =    <Flex alignItems="center" justifyContent="space-between" flexGrow="1">
                        <OnOff size="medium" disabled={props.disabled} active={ap.done} onClick={(active) => handleUpdateActionPoint({ ...ap, done: active })} />
                
                        <Button variant="destructive-text" icon="interface_actions_delete" disabled={props.disabled} onClick={() => props.onDelete && props.onDelete()} />
                        
                        {props.linkDisplay &&
                            <ActionPlanLinkEditor
                                siteId={props.linkDisplay.siteId || ''}
                                actionPlanId={props.linkDisplay.actionPlanId}
                                linkContext={{ type: 'actionPoint', actionPointId: ap.id }}
                                link={props.linkDisplay.links.find(l => l.configuration.type === 'actionPoint' && l.configuration.actionPointId === ap.id)}
                                onUpdated={props.onUpdated}
                            />
                        }
                    </Flex>;

    return (
        <>
            <LoadingPageOverlay loading={loading} />
            
            <Card variant="outlined" padding="8px">
                <Flex alignItems="center" justifyContent="space-between" flexWrap="wrap">
                    {col1}
                    <Flex alignItems="center" justifyContent="space-between" flexWrap="wrap">
                        {col2}
                        <Flex alignItems="center" flexWrap="wrap">
                                {col3}
                                {col4}
                            </Flex>
                    </Flex>
                </Flex>
            </Card>

        </>
    );
}